import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

/**
 * Hook to fetch alert notifications from Supabase
 * Queries the alert_notifications table which stores triggered alerts
 */
export const useAlerts = (limit = 10) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertsTodayCount, setAlertsTodayCount] = useState(0);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setAlerts([]);
      setAlertsTodayCount(0);
      setIsLoading(false);
      return;
    }

    const fetchAlerts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get today's date range
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // First, get user's sensor IDs
        const { data: userSensors, error: sensorsError } = await supabase
          .from('sensors')
          .select('id')
          .eq('user_id', user.id);

        if (sensorsError) throw sensorsError;

        const sensorIds = (userSensors || []).map(s => s.id);

        if (sensorIds.length === 0) {
          setAlerts([]);
          setAlertsTodayCount(0);
          setIsLoading(false);
          return;
        }

        // Fetch recent alert notifications with sensor info
        const { data: alertsData, error: alertsError } = await supabase
          .from('alert_notifications')
          .select(`
            *,
            sensors:sensor_id (
              id,
              name,
              device_id,
              zone_id,
              zones:zone_id (
                id,
                name
              )
            )
          `)
          .in('sensor_id', sensorIds)
          .order('created_at', { ascending: false })
          .limit(limit);

        if (alertsError) throw alertsError;

        // Count alerts today
        const { count: todayCount, error: countError } = await supabase
          .from('alert_notifications')
          .select('*', { count: 'exact', head: true })
          .in('sensor_id', sensorIds)
          .gte('created_at', today.toISOString())
          .lt('created_at', tomorrow.toISOString());

        if (countError) {
          console.warn('Error counting alerts today:', countError);
        } else {
          setAlertsTodayCount(todayCount || 0);
        }

        // Transform alerts data
        const transformedAlerts = (alertsData || []).map(alert => {
          const sensorName = alert.sensors?.name || 'Unknown Sensor';
          const zoneName = alert.sensors?.zones?.name || null;
          
          // Build message from available fields
          let message = alert.message;
          if (!message) {
            const parts = [];
            if (sensorName && sensorName !== 'Unknown Sensor') parts.push(sensorName);
            if (alert.alert_type) parts.push(alert.alert_type);
            if (alert.current_value !== undefined) {
              parts.push(`${alert.current_value.toFixed(1)}${alert.alert_type.includes('temp') ? '°C' : '%'}`);
            }
            message = parts.length > 0 ? parts.join(' - ') : 'Alert triggered';
          }

          // Determine severity based on alert type
          let severity = 'warning';
          if (alert.alert_type.includes('max')) {
            severity = 'high';
          } else if (alert.alert_type.includes('min')) {
            severity = 'medium';
          }

          return {
            id: alert.id,
            sensor_id: alert.sensor_id,
            zone_id: alert.sensors?.zone_id || null,
            sensor_name: sensorName,
            zone_name: zoneName,
            alert_type: alert.alert_type || 'warning',
            message: message,
            severity: severity,
            current_value: alert.current_value,
            threshold_value: alert.threshold_value,
            status: alert.status,
            created_at: alert.created_at,
            sent_at: alert.sent_at,
            is_resolved: alert.status === 'sent',
          };
        });

        setAlerts(transformedAlerts);
      } catch (err) {
        console.error('Error fetching alerts:', err);
        setError(err.message || 'Failed to fetch alerts');
        // If table doesn't exist, set empty array
        if (err.code === 'PGRST116' || err.message?.includes('does not exist')) {
          setAlerts([]);
          setAlertsTodayCount(0);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlerts();

    // Set up real-time subscription for alert notifications
    const subscription = supabase
      .channel('alert_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'alert_notifications',
        },
        () => {
          fetchAlerts();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, isAuthenticated, limit]);

  return {
    alerts,
    alertsTodayCount,
    isLoading,
    error,
    refetch: () => {
      if (isAuthenticated && user) {
        setIsLoading(true);
      }
    }
  };
};
